import React from 'react'
import WhoHero from '../Components/who/WhoHero'
import Overview from '../Components/diversity-inclusion/Overview'
import DiversityDrive from '../Components/diversity-inclusion/DiversityDrive'
import AccoladesAchievements from '../Components/diversity-inclusion/AccoladesAchievements'
import InclusiveWorkplace from '../Components/diversity-inclusion/InclusiveWorkplace'
import InclusiveFuture from '../Components/diversity-inclusion/InclusiveFuture'
import EqualOpportunity from '../Components/diversity-inclusion/EqualOpportunity'
import HiringUpskilling from '../Components/diversity-inclusion/HiringUpskilling'
import { motion } from 'framer-motion'
import { Helmet } from "react-helmet-async";

function DiversityInclusion() {
    const boxVariants = {
        hidden: { opacity: 0, scale: 0.98, y: 20 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
    };
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 },
        },
    };

    const fadeUpVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
    };
    return (
        <div>
            <Helmet>
    <title>
        Diversity & Inclusion | Building an Inclusive Workplace | Mobikode
    </title>

    <meta
        name="description"
        content="Learn about Mobikode's commitment to diversity and inclusion, equal opportunity, inclusive workplaces, employee development, and building a culture where everyone can thrive."
    />

    <meta
        name="keywords"
        content="Mobikode Diversity and Inclusion, Diversity and Inclusion, Inclusive Workplace, Equal Opportunity, Workplace Diversity, Employee Inclusion, Diversity Programs, Inclusive Culture, Women in Technology, Employee Upskilling, Mobikode Careers"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/diversity-inclusion"
    />

    <meta
        property="og:title"
        content="Diversity & Inclusion | Building an Inclusive Workplace | Mobikode"
    />

    <meta
        property="og:description"
        content="Discover Mobikode's diversity and inclusion initiatives focused on equal opportunity, inclusive workplaces, employee development, and a culture where every perspective matters."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/diversity-inclusion"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/diversity/bg3.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Diversity & Inclusion | Building an Inclusive Workplace | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Learn how Mobikode promotes diversity, inclusion, equal opportunity, employee growth, and an inclusive workplace culture."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/diversity/bg3.png"
    />
</Helmet>
            <WhoHero
                title="Diversity & Inclusion"
                text="United and diverse, we stand."
                imgData="/assets/diversity/bg3.png"
            />
            <Overview />
            <DiversityDrive />
            <AccoladesAchievements />
            <InclusiveWorkplace />
            <InclusiveFuture />
            <EqualOpportunity />
            <HiringUpskilling />
            <section className='overflow-hidden'>
                <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={boxVariants}
                        className="group w-full bg-white border border-[#BAC9D7] border-b-4 border-b-[#01386A] p-6 sm:p-10 md:p-12 lg:p-14 flex flex-col lg:flex-row justify-between items-stretch gap-8 lg:gap-12 transition-shadow duration-300 hover:shadow-md"
                    >
                        <div className="lg:max-w-[588px] w-full flex flex-col justify-between flex-1 h-auto min-h-full">
                            <h3 className="text-[20px] md:text-[30px] font-[600] text-[#01386A] font-sora mb-5 sm:mb-8 md:mb-12 leading-[1.25] tracking-tight break-words w-full">
                                We value our women employees
                            </h3>

                            <p className="text-[14px] md:text-[16px] text-[#49577A] font-graphik font-[500] leading-relaxed break-words w-full mt-auto lg:mt-0">
                                Over 33% of Cybage's employees are women, which is the highest rate in the country.

                                We have implemented the following programs & policies to support work-life balance for them:
                            </p>
                        </div>
                        <div className="lg:max-w-[529px] w-full shrink-0 flex justify-center lg:justify-end order-1 lg:order-2">
                            <div className="w-full max-w-[480px] lg:max-w-none aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/3] overflow-hidden relative bg-slate-100">
                                <motion.img
                                    whileHover={{ scale: 1.03 }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    src="/assets/diversity/img6.png"
                                    alt="Diversity and inclusion abstract green waves graphic mesh"
                                    className="w-full h-full object-cover absolute inset-0 transition-transform duration-500"
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
            <section className='lg:py-20 py-10 overflow-hidden'>
                <div className="max-w-[1440px] w-full mx-auto lg:px-[80px] px-[20px]">

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="flex flex-col lg:flex-row gap-6 justify-between lg:gap-16 items-start w-full"
                    >
                        <motion.div variants={fadeUpVariants} className="lg:max-w-[408px] w-full flex flex-col justify-between h-auto min-h-full">
                            <h2 className="text-[20px] md:text-[30px] font-[600] text-[#01386A] font-sora leading-[1.4] tracking-tight break-words w-full mb-4 lg:mb-0">
                                We are a data-science driven company
                            </h2>
                        </motion.div>
                        <motion.div variants={fadeUpVariants} className="lg:max-w-[666px] w-full flex flex-col justify-end h-auto min-h-full">
                            <p className="text-[14px] md:text-[16px] text-[#49577A] font-graphik font-[500] leading-relaxed break-words w-full">
                                Cybage firmly believes that competence trumps experience, and a competent employee is duly accredited.
                                Our desire to create a bias-free workplace where all perspectives are valued led us to build an in-house predictive analytics platform. This platform minimizes the effect of human biases while deciding the value of an employee to the firm, irrespective of gender, ethnicity, location, etc. This allows people from all communities to feel included and welcomed into the Cybage family.
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
        </div>
    )
}

export default DiversityInclusion
